import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Award, Clock, Heart, CheckCircle, Globe } from 'lucide-react';
const values = [{
  icon: Shield,
  title: '安全至上',
  en: 'Safety First',
  desc: '每一家入驻商家都经过严格的资质审核与实地考察，确保您的每一次体验都安全无忧。'
}, {
  icon: Award,
  title: '品质保障',
  en: 'Quality Assured',
  desc: '我们与新加坡顶级按摩师和水疗中心合作，只推荐经过认证的专业服务。'
}, {
  icon: Heart,
  title: '用心服务',
  en: 'Heartfelt Service',
  desc: '24/7全天候客服支持，从预约到体验，全程为您提供贴心的服务保障。'
}, {
  icon: Globe,
  title: '多元文化',
  en: 'Multicultural',
  desc: '汇聚中式推拿、泰式按摩、瑞典式等多国按摩文化，满足不同需求与偏好。'
}];
const milestones = [{
  year: '2018',
  event: '新加坡美丽城堡正式成立，开启专业按摩推荐之旅'
}, {
  year: '2020',
  event: '入驻商家突破200家，覆盖新加坡主要商圈'
}, {
  year: '2022',
  event: '累计服务用户超过30,000人，好评率达98%'
}, {
  year: '2024',
  event: '合作商家超过500家，成为新加坡领先的按摩推荐平台'
}, {
  year: '2026',
  event: '持续创新，致力于打造新加坡最值得信赖的健康生活平台'
}];
export function AboutSection() {
  return <section id="about" className="py-24 bg-white relative overflow-hidden" data-id="element-10">
      {/* Decorative background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-11" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-forest/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-12" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-13">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-14">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-forest mb-4" data-id="element-15">
            关于我们 <span className="text-gold font-light" data-id="element-16">About Us</span>
          </h2>
          <div className="w-20 h-1 bg-gold mx-auto rounded-full mb-8" data-id="element-17" />
        </motion.div>

        {/* Main Introduction */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-4xl mx-auto text-center mb-20" data-id="element-18">
          <p className="text-lg md:text-xl text-stone/80 leading-relaxed mb-6" data-id="element-19">
            <span className="font-serif font-bold text-forest text-2xl" data-id="element-20">
              新加坡美丽城堡（sgbeautycastle）
            </span>
            是新加坡领先的专业按摩推荐平台，自2018年创立以来，始终致力于为新加坡居民和游客提供安全、便捷、优质的按摩服务体验。
          </p>
          <p className="text-base md:text-lg text-stone/70 leading-relaxed mb-6" data-id="element-21">
            我们深知，在繁忙的都市生活中，每个人都需要一个值得信赖的放松方式。无论您是在乌节路购物后想要舒缓疲劳，
            还是在牛车水探索美食后寻找一次正宗的中式推拿，又或是在武吉士体验地道的泰式按摩——
            新加坡美丽城堡都能帮您找到最适合的选择。
          </p>
          <p className="text-base md:text-lg text-stone/70 leading-relaxed" data-id="element-22">
            我们与全岛超过<span className="font-bold text-forest" data-id="element-23">500家</span>
            经过严格认证的按摩商家合作， 累计为超过
            <span className="font-bold text-forest" data-id="element-24">50,000位</span>
            用户提供了满意的服务推荐。
            每一家商家都经过我们团队的实地考察和资质审核，确保服务质量和安全标准。
          </p>
        </motion.div>

        {/* Core Values */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-20" data-id="element-25">
          {values.map((value, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="text-center p-6" data-id="element-26">
              <div className="w-16 h-16 bg-cream rounded-2xl flex items-center justify-center mx-auto mb-5 text-gold" data-id="element-27">
                <value.icon size={32} strokeWidth={1.5} data-id="element-28" />
              </div>
              <h3 className="font-serif font-bold text-forest text-xl mb-1" data-id="element-29">
                {value.title}
              </h3>
              <p className="text-gold text-sm font-medium mb-3" data-id="element-30">{value.en}</p>
              <p className="text-stone/70 text-sm leading-relaxed" data-id="element-31">
                {value.desc}
              </p>
            </motion.div>)}
        </div>

        {/* Platform Story / Milestones */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-cream/50 rounded-3xl p-8 md:p-12 border border-stone/5" data-id="element-32">
          <h3 className="font-serif font-bold text-forest text-2xl md:text-3xl text-center mb-10" data-id="element-33">
            我们的历程 <span className="text-gold font-light" data-id="element-34">Our Journey</span>
          </h3>

          <div className="relative" data-id="element-35">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gold/20 md:-translate-x-px" data-id="element-36" />

            <div className="space-y-8" data-id="element-37">
              {milestones.map((milestone, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: index % 2 === 0 ? -20 : 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className={`relative flex items-start gap-6 md:gap-0 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-38">
                  {/* Content */}
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'}`} data-id="element-39">
                    <span className="inline-block font-serif font-bold text-gold text-xl mb-1" data-id="element-40">
                      {milestone.year}
                    </span>
                    <p className="text-stone/80 text-sm leading-relaxed" data-id="element-41">
                      {milestone.event}
                    </p>
                  </div>

                  {/* Dot */}
                  <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-gold rounded-full -translate-x-1.5 mt-2 ring-4 ring-cream/50" data-id="element-42" />

                  {/* Spacer for opposite side */}
                  <div className="hidden md:block flex-1" data-id="element-43" />
                </motion.div>)}
            </div>
          </div>
        </motion.div>

        {/* Platform Advantages */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-20 max-w-4xl mx-auto" data-id="element-44">
          <h3 className="font-serif font-bold text-forest text-2xl md:text-3xl text-center mb-10" data-id="element-45">
            为什么选择我们{' '}
            <span className="text-gold font-light" data-id="element-46">Why Choose Us</span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-47">
            {[{
            title: '严格商家审核',
            desc: '所有入驻商家必须通过营业执照验证、卫生许可检查和服务质量评估三重审核。'
          }, {
            title: '真实用户评价',
            desc: '所有评价均来自真实消费用户，杜绝虚假好评，让您做出明智的选择。'
          }, {
            title: '覆盖全岛商圈',
            desc: '从乌节路到牛车水，从武吉士到丹戎巴葛，覆盖新加坡所有主要区域。'
          }, {
            title: '多元服务类型',
            desc: '中式推拿、泰式按摩、足部按摩、精油SPA、运动按摩、情侣按摩等一应俱全。'
          }, {
            title: '透明价格体系',
            desc: '所有商家明码标价，无隐藏消费，让您安心享受每一次服务。'
          }, {
            title: '专业客服团队',
            desc: '中英双语客服团队全天候在线，随时解答您的疑问，处理您的需求。'
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className="flex items-start gap-4 p-4" data-id="element-48">
                <CheckCircle className="text-gold shrink-0 mt-0.5" size={20} data-id="element-49" />
                <div data-id="element-50">
                  <h4 className="font-bold text-forest mb-1" data-id="element-51">{item.title}</h4>
                  <p className="text-stone/70 text-sm leading-relaxed" data-id="element-52">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}